import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  Moon,
  Sun,
  LogOut,
  Globe,
  UserCircle2,
  Monitor,
  History,
  Shield,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@components/ui';
import { useUIStore } from '@stores/ui';
import { useAuthStore } from '@stores/auth';
import { SUPPORTED_LANGUAGES } from '@lib/i18n';
import { authApi } from '@features/auth/api';
import { tokenStorage } from '@lib/auth/storage';
import { cn } from '@lib/utils/cn';
import { NotificationBell } from '@features/notifications';
import { QuickAccessHomeButton } from '@features/quick-access/QuickAccessHomeButton';

/**
 * Language names for the switcher. Bilingual on purpose: the English UI must
 * not render Arabic script, so each language is named in the *current* UI
 * language (Arabic mode keeps the native endonyms it always showed).
 */
const LANG_LABELS: Record<string, { ar: string; en: string }> = {
  ar: { ar: 'العربية', en: 'Arabic' },
  en: { ar: 'English', en: 'English' },
  ku: { ar: 'کوردی', en: 'Kurdish' },
  al: { ar: 'Shqip', en: 'Albanian' },
};

function langLabel(code: string, isRtl: boolean): string {
  const pair = LANG_LABELS[code];
  if (!pair) return code;
  return isRtl ? pair.ar : pair.en;
}

export function Header() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const navigate = useNavigate();
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [langOpen, setLangOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);

  const isDark = theme === 'dark';

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  async function handleLogout() {
    setUserMenuOpen(false);
    const refreshToken = tokenStorage.getRefreshToken();
    if (refreshToken) {
      try {
        await authApi.logout(refreshToken);
      } catch {
        /* best-effort cleanup */
      }
    }
    logout();
    navigate('/login', { replace: true });
  }

  // Prefer human-readable display_name (set in /account/profile), then fall
  // back to login name and finally email so the dropdown is never blank.
  const displayName =
    user?.display_name ?? user?.name ?? user?.user_name ?? user?.userName ?? user?.email ?? '?';
  // Secondary line shown under the name. Show company first (more recognizable
  // for tenants); fall back to email so it never duplicates the primary line.
  const secondaryLine = user?.companyName ?? user?.email ?? '';
  const sameAsName = secondaryLine === displayName;
  // اسم المستخدم/الاسم الكامل للحساب المُسجَّل — يُعرض في القائمة المنسدلة بدل
  // الإيميل. نُفضّل اسم الدخول (user_name) ثم الاسم، دون اللجوء للإيميل.
  const loginName =
    user?.user_name ?? user?.userName ?? user?.name ?? user?.display_name ?? '';
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <header className="sticky top-0 z-20 h-16 border-b border-border bg-surface/80 backdrop-blur">
      <div className="h-full flex items-center gap-3 px-4 md:px-6">
        <Button variant="ghost" size="icon" onClick={toggleSidebar} aria-label="Toggle sidebar">
          <Menu className="h-5 w-5" />
        </Button>

        <div className="flex-1" />

        <div className="relative" ref={langMenuRef}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLangOpen((v) => !v)}
            leftIcon={<Globe className="h-4 w-4" />}
          >
            {langLabel(i18n.language, isRtl)}
          </Button>
          {langOpen && (
            <div className="absolute end-0 mt-2 w-44 rounded-md border border-border bg-surface shadow-elevated py-1 z-30 animate-slide-in-up">
              {SUPPORTED_LANGUAGES.map((lng) => (
                <button
                  key={lng}
                  className={cn(
                    'w-full text-start px-3 py-2 text-sm hover:bg-bg-muted flex items-center justify-between',
                    i18n.language === lng && 'bg-brand-muted text-brand',
                  )}
                  onClick={() => {
                    void i18n.changeLanguage(lng);
                    setLangOpen(false);
                  }}
                >
                  <span>{langLabel(lng, isRtl)}</span>
                  <span className="text-[11px] text-fg-subtle uppercase">{lng}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(isDark ? 'light' : 'dark')}
          aria-label="Toggle theme"
        >
          {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>

        {/* Quick Access shortcut — opens the user's home screen (or their only
            screen, else the launcher). Hidden when they have no screens. */}
        <QuickAccessHomeButton />

        {/* Notification bell — mounted before the user menu so the badge sits
            visually grouped with the identity actions. Self-gates on the
            `showNotifications` permission. */}
        <NotificationBell />

        {user && (
          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setUserMenuOpen((v) => !v)}
              className="flex items-center gap-3 ps-3 border-s border-border hover:text-brand transition-colors"
            >
              <div className="text-end hidden sm:block max-w-[180px]">
                <div className="text-sm font-medium leading-none truncate">{displayName}</div>
                {secondaryLine && !sameAsName && (
                  <div className="text-xs text-fg-muted mt-1 truncate">{secondaryLine}</div>
                )}
              </div>
              <div className="h-9 w-9 rounded-full bg-brand-muted text-brand grid place-items-center font-semibold">
                {initial}
              </div>
              <ChevronDown className="h-4 w-4 text-fg-muted" />
            </button>

            {userMenuOpen && (
              <div className="absolute end-0 mt-2 w-72 rounded-md border border-border bg-surface shadow-elevated py-1 z-30 animate-slide-in-up">
                {/* Identity header — name large, company secondary, username tertiary */}
                <div className="px-3 py-3 border-b border-border">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 shrink-0 rounded-full bg-brand-muted text-brand grid place-items-center font-semibold text-base">
                      {initial}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-fg truncate">{displayName}</div>
                      {user.companyName && (
                        <div className="text-xs text-fg-muted truncate mt-0.5">
                          {user.companyName}
                        </div>
                      )}
                      {loginName && loginName !== displayName && (
                        <div className="text-[11px] text-fg-subtle truncate mt-0.5">
                          {loginName}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <MenuLink
                  icon={<UserCircle2 className="h-4 w-4" />}
                  label={t('account.profile', isRtl ? 'الملف الشخصي' : 'Profile')}
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/account/profile');
                  }}
                />
                <MenuLink
                  icon={<Monitor className="h-4 w-4" />}
                  label={t('account.sessions', isRtl ? 'الجلسات النشطة' : 'Active sessions')}
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/account/sessions');
                  }}
                />
                <MenuLink
                  icon={<History className="h-4 w-4" />}
                  label={t('account.loginHistory', isRtl ? 'سجل الدخول' : 'Login history')}
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/account/login-history');
                  }}
                />
                <MenuLink
                  icon={<Shield className="h-4 w-4" />}
                  label={t('account.permissions', isRtl ? 'الصلاحيات' : 'Permissions')}
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/account/permissions');
                  }}
                />
                <div className="my-1 border-t border-border" />
                <MenuLink
                  icon={<LogOut className="h-4 w-4" />}
                  label={t('common.logout', isRtl ? 'تسجيل الخروج' : 'Logout')}
                  onClick={handleLogout}
                  danger
                />
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

function MenuLink({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: React.ReactNode;
  label: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-start flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-bg-muted transition-colors',
        danger ? 'text-danger' : 'text-fg',
      )}
    >
      <span className="text-fg-muted shrink-0">{icon}</span>
      {label}
    </button>
  );
}
